/**
 * Running system: what a Team's running copy of the customer's system is made
 * of and how it went, from `GET /api/system-environments/teams/:teamId`.
 *
 *   headline       Working, Hybrid (with the parts that run outside the copy
 *                  named) or Could not run (with the cause in the closed
 *                  list's words); when it was last refreshed
 *   parts          every component: what it is, how it runs (built from a
 *                  repository, an image, or remote), its state and why
 *   how it differs provenance lines as Explore wrote them, e.g. a private
 *                  package built from a sibling repository
 *   needs          what only a person can answer; an admin enters a value
 *                  (never shown again), marks a need provided, declines it
 *                  (that part stays remote) or reopens it, through the
 *                  needs routes (`systemApi.ts`)
 *   pull requests  the copy's runs per pull request; the video link shows
 *                  only to readers who can open Fletcher's evidence
 *                  (`/api/qa/evidence/:id` gates on repository
 *                  administration), the same `viewer.canProvide`
 *
 * Markup only: the words are `systemCopy.ts`. The panel never holds a value
 * after it is sent, and the record it reads carries none.
 */
import { useState } from "react";
import type {
  SystemClaimView,
  SystemComponent,
  SystemEnvironmentReadResponse,
  SystemNeed,
} from "@yagni/shared";
import { Btn, Chip } from "../../queue/index.js";
import {
  ANSWER_FAILED,
  COMPONENT_KIND_WORD,
  COMPONENT_STATE_WORD,
  DECLINE,
  DECLINED,
  DECLINED_NOTE,
  ESTIMATE,
  MAP_EMPTY,
  MARK_ADMIN_ONLY,
  MARK_PROVIDED,
  NEED_KIND_WORD,
  NO_CLAIMS,
  NO_NEEDS,
  PROVENANCE_KIND_WORD,
  PROVIDE_ADMIN_ONLY,
  PROVIDE_CANCEL,
  PROVIDE_FAILED,
  PROVIDE_NOTE,
  PROVIDE_PICK_REPO,
  PROVIDE_SAVE,
  PROVIDE_SAVED,
  PROVIDE_TOO_SHORT,
  PROVIDE_VALUE,
  PROVIDED,
  REOPEN,
  SECTION_TITLE,
  WATCH_VIDEO,
  activityLine,
  capabilityLine,
  claimOutcomeWords,
  componentCause,
  coverageWords,
  freshnessLine,
  howItRuns,
  memoryWords,
  runsOnItsOwn,
  systemHeadline,
} from "./systemCopy.js";
import {
  NEED_VALUE_MAX_LENGTH,
  NEED_VALUE_MIN_LENGTH,
  needAnswer,
  type SystemApi,
} from "./systemApi.js";
import "../../workers/workers.css";
import "../../settings/repositoryEnvironment.css";

const LIST = "wrk-preflight-list";

const STATE_CLASS: Partial<Record<SystemComponent["state"], string>> = {
  working: "is-ok",
  needs_you: "is-warning",
  could_not_run: "is-missing",
};

const STATE_MARK: Record<SystemComponent["state"], string> = {
  not_set_up: "○",
  setting_up: "·",
  needs_you: "!",
  working: "✓",
  remote: "↗",
  could_not_run: "!",
};

export interface SystemPanelProps {
  teamId: string;
  data: SystemEnvironmentReadResponse;
  api: SystemApi;
  /** Called after a need is answered, so the record is read again. */
  onChanged: () => void;
  /** Prints the Team's name in the head, for the repository sheet that lists several. */
  teamName?: string;
  /** The panel sits in a fold that already carries the section's title. */
  inFold?: boolean;
  now?: number;
}

export function SystemPanel({
  teamId,
  data,
  api,
  onChanged,
  teamName,
  inFold,
  now,
}: SystemPanelProps) {
  const { environment, components, needs, claims, viewer } = data;
  const freshness = freshnessLine(environment, now);
  const activity = activityLine(environment);
  const memory = memoryWords(environment?.memoryEstimateMb);
  const systemProvenance = (environment?.provenance ?? []).filter(
    (line) => line.componentKey === null,
  );
  const repoChoices = repositoriesOf(components);
  const ordered = [...needs].sort((a, b) => needRank(a) - needRank(b));

  return (
    <div className="blk" data-testid="running-system" data-team={teamId}>
      <div className="bh">
        {!inFold && (
          <span className="label">
            {teamName ? `${SECTION_TITLE}: ${teamName}` : SECTION_TITLE}
          </span>
        )}
        {freshness && (
          <span className="fact" data-testid="running-system-freshness">
            {freshness}
          </span>
        )}
      </div>
      <p data-testid="running-system-headline" data-state={environment?.state ?? "none"}>
        {systemHeadline(environment, components)}
      </p>
      {activity && <p className="qa-hint">{activity}</p>}

      <dl className="qa-summary">
        {memory && (
          <div>
            <dt>Memory</dt>
            <dd data-testid="running-system-memory">{memory}</dd>
          </div>
        )}
        {environment && (
          <div>
            <dt>Parts</dt>
            <dd>
              {components.length === 0 ? (
                <p className="qa-hint">{MAP_EMPTY}</p>
              ) : (
                <ul className={LIST} aria-label="Parts" data-testid="running-system-parts">
                  {components.map((component) => (
                    <ComponentRow
                      key={component.key}
                      component={component}
                      provenance={(environment.provenance ?? []).filter(
                        (line) => line.componentKey === component.key,
                      )}
                    />
                  ))}
                </ul>
              )}
            </dd>
          </div>
        )}
        {systemProvenance.length > 0 && (
          <div>
            <dt>How the copy differs</dt>
            <dd>
              <ul
                className={LIST}
                aria-label="How the copy differs"
                data-testid="running-system-provenance"
              >
                {systemProvenance.map((line, index) => (
                  <li key={`${line.at}:${index}`}>
                    <span className="wrk-preflight-text">
                      <span>{PROVENANCE_KIND_WORD[line.kind]}</span>
                      <span className="qa-fact">{line.text}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
        {environment && (
          <div>
            <dt>Needs</dt>
            <dd>
              {ordered.length === 0 ? (
                <p className="qa-hint">{NO_NEEDS}</p>
              ) : (
                <ul className={LIST} aria-label="Needs" data-testid="running-system-needs">
                  {ordered.map((need) => (
                    <NeedRow
                      key={need.id}
                      need={need}
                      teamId={teamId}
                      api={api}
                      canProvide={viewer.canProvide}
                      repoChoices={repoChoices}
                      fixedRepo={repoIdForNeed(need, components)}
                      onChanged={onChanged}
                    />
                  ))}
                </ul>
              )}
            </dd>
          </div>
        )}
        {environment && (
          <div>
            <dt>Pull requests</dt>
            <dd>
              {claims.length === 0 ? (
                <p className="qa-hint">{NO_CLAIMS}</p>
              ) : (
                <ul className={LIST} aria-label="Pull requests" data-testid="running-system-claims">
                  {claims.map((claim) => (
                    <ClaimRow key={claim.id} claim={claim} canOpenEvidence={viewer.canProvide} />
                  ))}
                </ul>
              )}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

function ComponentRow({
  component,
  provenance,
}: {
  component: SystemComponent;
  provenance: NonNullable<SystemEnvironmentReadResponse["environment"]>["provenance"];
}) {
  const cause = componentCause(component);
  const estimate = component.evidence.length === 0;
  return (
    <li
      className={STATE_CLASS[component.state]}
      data-testid="running-system-part"
      data-key={component.key}
      data-state={component.state}
    >
      <span className="wrk-preflight-mark" aria-hidden="true">
        {STATE_MARK[component.state]}
      </span>
      <span className="wrk-preflight-text">
        <span>
          <b>{component.name}</b>{" "}
          <span className="qa-hint">{COMPONENT_KIND_WORD[component.kind]}</span>
        </span>
        <span className="qa-fact">{howItRuns(component.source)}</span>
        {runsOnItsOwn(component.kind) && <Chip>{COMPONENT_STATE_WORD[component.state]}</Chip>}
        {estimate && <span className="qa-hint">{ESTIMATE}</span>}
        {cause && <span data-testid="running-system-part-cause">{cause}</span>}
        {provenance.map((line, index) => (
          <span
            key={`${line.at}:${index}`}
            className="qa-hint"
            data-testid="running-system-part-provenance"
          >
            {PROVENANCE_KIND_WORD[line.kind]}: {line.text}
          </span>
        ))}
      </span>
    </li>
  );
}

function needRank(need: SystemNeed): number {
  return need.status === "open" ? 0 : need.status === "provided" ? 1 : 2;
}

interface RepoChoice {
  repoId: string;
  label: string;
}

/** The repositories the map builds from: where a credential's value can belong. */
function repositoriesOf(components: readonly SystemComponent[]): RepoChoice[] {
  const seen = new Map<string, string>();
  for (const component of components) {
    if (!component.repoId || seen.has(component.repoId)) continue;
    seen.set(
      component.repoId,
      component.source.kind === "build" ? component.source.repo : component.name,
    );
  }
  return [...seen].map(([repoId, label]) => ({ repoId, label }));
}

/** A need that names a part built from a repository belongs to that repository. */
function repoIdForNeed(need: SystemNeed, components: readonly SystemComponent[]): string | null {
  const component = need.componentKey
    ? components.find((c) => c.key === need.componentKey)
    : undefined;
  return component?.repoId ?? null;
}

function NeedRow({
  need,
  teamId,
  api,
  canProvide,
  repoChoices,
  fixedRepo,
  onChanged,
}: {
  need: SystemNeed;
  teamId: string;
  api: SystemApi;
  canProvide: boolean;
  repoChoices: readonly RepoChoice[];
  fixedRepo: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const status =
    need.status === "provided" ? PROVIDED : need.status === "declined" ? DECLINED : null;
  const answer = needAnswer(need);
  const isOpen = need.status === "open";

  /** Decline, reopen and mark provided: one post, then the record is read again. */
  async function act(run: () => Promise<void>) {
    setBusy(true);
    setNote(null);
    try {
      await run();
      onChanged();
    } catch {
      setNote(ANSWER_FAILED);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li
      className={isOpen ? "is-warning" : need.status === "provided" ? "is-ok" : undefined}
      data-testid="running-system-need"
      data-status={need.status}
      data-kind={need.kind}
      data-need={need.id}
    >
      <span className="wrk-preflight-mark" aria-hidden="true">
        {isOpen ? "!" : need.status === "provided" ? "✓" : "–"}
      </span>
      <span className="wrk-preflight-text">
        <span>
          <code>{need.name}</code> <span className="qa-hint">{NEED_KIND_WORD[need.kind]}</span>
        </span>
        {need.kind === "capability" ? (
          <span className="qa-hint">{capabilityLine(need.capabilityKey)}</span>
        ) : (
          need.detail && <span className="qa-fact">{need.detail}</span>
        )}
        {status && <Chip>{status}</Chip>}
        {need.status === "declined" && <span className="qa-hint">{DECLINED_NOTE}</span>}
        {isOpen && !canProvide && answer !== "none" && (
          <span className="qa-hint">
            {answer === "enter_value" ? PROVIDE_ADMIN_ONLY : MARK_ADMIN_ONLY}
          </span>
        )}
        {canProvide && !open && (
          <span className="qa-ask">
            {isOpen && answer === "enter_value" && (
              <Btn
                size="sm"
                disabled={busy}
                onClick={() => {
                  setNote(null);
                  setOpen(true);
                }}
                data-testid="running-system-provide"
              >
                {PROVIDE_VALUE}
              </Btn>
            )}
            {isOpen && answer === "mark_provided" && (
              <Btn
                size="sm"
                disabled={busy}
                onClick={() => void act(() => api.markNeedProvided(teamId, need.id))}
                data-testid="running-system-mark-provided"
              >
                {MARK_PROVIDED}
              </Btn>
            )}
            {isOpen && (
              <Btn
                size="sm"
                variant="quiet"
                disabled={busy}
                onClick={() => void act(() => api.declineNeed(teamId, need.id))}
                data-testid="running-system-decline"
              >
                {DECLINE}
              </Btn>
            )}
            {!isOpen && (
              <Btn
                size="sm"
                variant="quiet"
                disabled={busy}
                onClick={() => void act(() => api.reopenNeed(teamId, need.id))}
                data-testid="running-system-reopen"
              >
                {REOPEN}
              </Btn>
            )}
          </span>
        )}
        {canProvide && open && isOpen && answer === "enter_value" && (
          <ProvideForm
            need={need}
            teamId={teamId}
            api={api}
            repoChoices={repoChoices}
            fixedRepo={fixedRepo}
            onClose={() => setOpen(false)}
            onSaved={() => {
              setOpen(false);
              setNote(PROVIDE_SAVED);
              onChanged();
            }}
          />
        )}
        {note && (
          <span className="qa-hint" role="status">
            {note}
          </span>
        )}
      </span>
    </li>
  );
}

/**
 * The write-only form for one credential: an optional repository pick, a
 * password field, Save and Cancel. A saved or cancelled value is dropped; a
 * failed save keeps it in the masked field only, so the person can retry
 * without typing it again. It is never printed as text.
 */
function ProvideForm({
  need,
  teamId,
  api,
  repoChoices,
  fixedRepo,
  onClose,
  onSaved,
}: {
  need: SystemNeed;
  teamId: string;
  api: SystemApi;
  repoChoices: readonly RepoChoice[];
  fixedRepo: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState("");
  const [repoId, setRepoId] = useState<string>(fixedRepo ?? repoChoices[0]?.repoId ?? "");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function save() {
    if (!long(value) || !repoId) return;
    setBusy(true);
    setFailed(false);
    try {
      await api.provideNeed(teamId, need.id, { secret: value, repoId });
      setValue("");
      onSaved();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="qa-ask" data-testid="running-system-provide-form">
      {!fixedRepo && repoChoices.length > 1 && (
        <select
          aria-label={PROVIDE_PICK_REPO}
          value={repoId}
          disabled={busy}
          onChange={(event) => setRepoId(event.target.value)}
        >
          {repoChoices.map((choice) => (
            <option key={choice.repoId} value={choice.repoId}>
              {choice.label}
            </option>
          ))}
        </select>
      )}
      <input
        className="qa-ask-value"
        type="password"
        aria-label={`Value for ${need.name}`}
        value={value}
        disabled={busy}
        autoComplete="new-password"
        maxLength={NEED_VALUE_MAX_LENGTH}
        spellCheck={false}
        onChange={(event) => setValue(event.target.value)}
      />
      <Btn
        size="sm"
        variant="primary"
        disabled={busy || !long(value) || !repoId}
        onClick={() => void save()}
      >
        {PROVIDE_SAVE}
      </Btn>
      <Btn
        size="sm"
        variant="quiet"
        disabled={busy}
        onClick={() => {
          setValue("");
          onClose();
        }}
      >
        {PROVIDE_CANCEL}
      </Btn>
      <span className="qa-hint">{PROVIDE_NOTE}</span>
      {value.length > 0 && !long(value) && <span className="qa-hint">{PROVIDE_TOO_SHORT}</span>}
      {failed && (
        <span className="qa-hint" role="status">
          {PROVIDE_FAILED}
        </span>
      )}
    </span>
  );
}

/** The provide route refuses a value outside these bounds; the copy's redactor leaves a short one alone. */
function long(value: string): boolean {
  return value.length >= NEED_VALUE_MIN_LENGTH && value.length <= NEED_VALUE_MAX_LENGTH;
}

function ClaimRow({
  claim,
  canOpenEvidence,
}: {
  claim: SystemClaimView;
  canOpenEvidence: boolean;
}) {
  const coverage = coverageWords(claim.coverage);
  const prHref = claim.repoFullName
    ? `https://github.com/${claim.repoFullName}/pull/${claim.prNumber}`
    : null;
  const label = claim.repoFullName
    ? `${claim.repoFullName}#${claim.prNumber}`
    : `#${claim.prNumber}`;
  return (
    <li
      className={
        claim.outcome === "recorded"
          ? "is-ok"
          : claim.outcome === "could_not_run"
            ? "is-missing"
            : undefined
      }
      data-testid="running-system-claim"
      data-outcome={claim.outcome ?? "open"}
    >
      <span className="wrk-preflight-text">
        <span>
          {prHref ? (
            <a href={prHref} target="_blank" rel="noreferrer">
              {label}
            </a>
          ) : (
            label
          )}{" "}
          <code>{claim.headSha.slice(0, 7)}</code>
        </span>
        <span>{claimOutcomeWords(claim.outcome, claim.causeCode)}</span>
        {coverage && <span className="qa-hint">{coverage}</span>}
        {claim.evidenceId && canOpenEvidence && (
          <a
            href={`/api/qa/evidence/${encodeURIComponent(claim.evidenceId)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="running-system-evidence"
          >
            {WATCH_VIDEO}
          </a>
        )}
      </span>
    </li>
  );
}
